import React from 'react'
import { Link } from 'gatsby'
import {
  Layout,
  Intro,
  Showcase,
  Header,
  SectionTitle,
  ItemTitle,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      >
        <Intro>
          <p>
            Notifications deliver page-level contextual help to guide the user
            through a task. Text appears within a color-coded container to give
            the message visual impact. Add an icon to draw additional focus to
            the notification. Use these classes only with{' '}
            <span class="inline__code">{'<div>'}</span> elements.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>
        <p>
          Notifications appear either as soon as the page loads or as triggered
          by an input field blur state. A notification will push all content
          below it down rather than appearing fixed above it.
        </p>
        <Showcase
          site={data.site}
          title="Info"
          text="Use this type of notification when providing general info that doesn’t require any action from the user."
          code={code.notificationInfo}
        >
          <div class="c-notification--info l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--info"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
          <div class="c-notification--inline-info l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--info"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Success"
          text="Use this type of notification to let the user know that an action they triggered has been completed successfully."
          code={code.notificationSuccess}
        >
          <div class="c-notification--success l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--success"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
          <div class="c-notification--inline-success l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--success"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Warning"
          text="Use this type of notification to let the user know that additional action is required before they can continue."
          code={code.notificationWarning}
        >
          <div class="c-notification--warning l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--exclamation"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
          <div class="c-notification--inline-warning l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--exclamation"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Error"
          text="	Use this type of notification to communicate major system issues or vital information that requires action from the user. Save for situations where there is risk of data loss or permanent termination from a process."
          code={code.notificationError}
        >
          <div class="c-notification--error l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--error"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
              dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
              nec tincidunt velit quam ac sem.
            </div>
          </div>
          <div class="c-notification--inline-error l-margin-bottom--medium">
            <div class="c-notification__icon">
              <i class="icon icon--error"></i>
            </div>
            <div class="c-notification__content">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </div>
          </div>
        </Showcase>
        <SectionTitle>Design Environment</SectionTitle>
        <ItemTitle>Elevation</ItemTitle>
        <p>
          Inline notifications will push all content below them down rather than
          appearing elevated above it. Page-level notifications can appear
          either embedded at the top of the page, or elevated on the{' '}
          <Link
            class="link"
            to={'/' + data.site + '/guidelines/elevation/design'}
          >
            attention pane
          </Link>{' '}
          to allow page content to scroll behind. When elevated, the
          notification will use a medium{' '}
          <Link class="link" to={'/' + data.site + '/utilities/styles/design'}>
            shadow
          </Link>
          .
        </p>
        <p>
          Generally, you should elevate a notification with information that the
          user will need to remain aware of as they navigate the page content,
          for example, a page-level error that can be resolved from two or more
          parts of the page. Notifications that contain information that is not
          pertinent to the user's task, for example a page-level information
          notification of an upcoming system outage, should be embedded within
          the layout so as not to distract the user from the page content
          they're interacting with.
        </p>
      </Layout>
    </>
  )
}

export const code = {
  notificationInfo: `<div class="c-notification--info">
  <div class="c-notification__icon">
    <i class="icon icon--info"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
    dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
    nec tincidunt velit quam ac sem.
  </div>
</div>
<div class="c-notification--inline-info">
  <div class="c-notification__icon">
    <i class="icon icon--info"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
  </div>
</div>
`,
  notificationSuccess: `<div class="c-notification--success">
  <div class="c-notification__icon">
    <i class="icon icon--success"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
    dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
    nec tincidunt velit quam ac sem.
  </div>
</div>
<div class="c-notification--inline-success">
  <div class="c-notification__icon">
    <i class="icon icon--success"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
  </div>
</div>
`,
  notificationWarning: `<div class="c-notification--warning">
  <div class="c-notification__icon">
    <i class="icon icon--exclamation"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
    dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
    nec tincidunt velit quam ac sem.
  </div>
</div>
<div class="c-notification--inline-warning">
  <div class="c-notification__icon">
    <i class="icon icon--exclamation"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
  </div>
</div>
`,
  notificationError: `<div class="c-notification--error">
  <div class="c-notification__icon">
    <i class="icon icon--error"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
    dictum, nisi sed commodo vulputate, arcu libero imperdiet nisi,
    nec tincidunt velit quam ac sem.
  </div>
</div>
<div class="c-notification--inline-error">
  <div class="c-notification__icon">
    <i class="icon icon--error"></i>
  </div>
  <div class="c-notification__content">
    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
  </div>
</div>
`,
}
